import React, { useState, useEffect } from "react";
import {
  apiService,
  ErrorMessage,
  Campaign,
  GetCampaignsResponse,
} from "../../services";

interface ErrorItem {
  id: string;
  name: string;
  message: string;
}

interface ErrorScenario {
  id: string;
  type: "personal" | "code" | "system";
  name: string;
  items: ErrorItem[];
}

const ErrorHandling: React.FC = () => {
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [editingMessages, setEditingMessages] = useState<
    Record<string, string>
  >({});
  const [hasChanges, setHasChanges] = useState(false);
  const [usingMockData, setUsingMockData] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState<boolean>(true);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");

  // Mock data for error scenarios (keeping the existing structure)
  const [errorScenarios, setErrorScenarios] = useState<ErrorScenario[]>([
    {
      id: "1",
      type: "personal",
      name: "Thông báo lỗi cá nhân",
      items: [
        {
          id: "1-1",
          name: "SĐT không hợp lệ",
          message:
            "Số điện thoại bạn nhập không đúng định dạng. Vui lòng nhập lại số điện thoại hợp lệ (VD: 0123456789).",
        },
        {
          id: "1-2",
          name: "Thiếu thông tin bắt buộc",
          message:
            "Vui lòng điền đầy đủ các thông tin bắt buộc được đánh dấu (*) trước khi tiếp tục.",
        },
        {
          id: "1-3",
          name: "Email không đúng định dạng",
          message:
            "Email bạn nhập không đúng định dạng. Vui lòng kiểm tra và nhập lại email hợp lệ.",
        },
        {
          id: "1-4",
          name: "Thông tin cá nhân không chính xác",
          message:
            "Thông tin cá nhân không chính xác. Vui lòng kiểm tra và cập nhật lại thông tin.",
        },
      ],
    },
    {
      id: "2",
      type: "code",
      name: "Thông báo lỗi mã",
      items: [
        {
          id: "2-1",
          name: "Mã sai hoặc không tồn tại",
          message:
            "Mã bạn nhập không tồn tại hoặc không chính xác. Vui lòng kiểm tra lại mã và thử lại.",
        },
        {
          id: "2-2",
          name: "Mã đã được sử dụng",
          message:
            "Mã này đã được sử dụng trước đó. Vui lòng sử dụng mã khác hoặc liên hệ hỗ trợ.",
        },
        {
          id: "2-3",
          name: "Mã hết hạn",
          message:
            "Mã này đã hết hạn. Vui lòng sử dụng mã mới hoặc liên hệ để được hỗ trợ.",
        },
        {
          id: "2-4",
          name: "Nhiều lỗi cùng lúc",
          message:
            "Có nhiều lỗi xảy ra. Vui lòng kiểm tra lại tất cả thông tin và thử lại.",
        },
      ],
    },
    {
      id: "3",
      type: "system",
      name: "Thông báo lỗi hệ thống",
      items: [
        {
          id: "3-1",
          name: "Chiến dịch đã tắt",
          message:
            "Chiến dịch này đã tạm dừng. Vui lòng thử lại sau hoặc liên hệ admin để biết thêm thông tin.",
        },
        {
          id: "3-2",
          name: "Rate limit vượt quá",
          message:
            "Bạn đã vượt quá giới hạn thao tác. Vui lòng thử lại sau vài phút.",
        },
        {
          id: "3-3",
          name: "Lỗi kết nối database",
          message:
            "Hệ thống đang gặp sự cố kỹ thuật. Vui lòng thử lại sau hoặc liên hệ hỗ trợ.",
        },
        {
          id: "3-4",
          name: "Lỗi xác thực",
          message: "Token đã hết hạn. Vui lòng đăng nhập lại để tiếp tục.",
        },
      ],
    },
  ]);

  // Fetch campaigns and then error messages for selected campaign
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        setCampaignsLoading(true);
        const resp: GetCampaignsResponse = await apiService.getCampaigns();
        if (resp.success && resp.data) {
          setCampaigns(resp.data);
          // Auto select first campaign if none selected
          if (!selectedCampaignId && resp.data.length > 0) {
            setSelectedCampaignId(resp.data[0]._id || "");
          }
        }
      } catch (err) {
        console.error("Failed to fetch campaigns:", err);
      } finally {
        setCampaignsLoading(false);
      }
    };
    loadCampaigns();
  }, []);

  useEffect(() => {
    const fetchErrorMessages = async () => {
      if (!selectedCampaignId) return; // require a campaign selection
      try {
        setIsLoading(true);
        const response = await apiService.getErrorMessages(selectedCampaignId);
        setErrorMessages(response.data);

        const initialMessages: Record<string, string> = {};
        response.data.forEach((msg) => {
          initialMessages[msg.error_id] = msg.message;
        });
        setEditingMessages(initialMessages);
      } catch (error: any) {
        console.error("Failed to fetch error messages:", error);
        if (
          error.message &&
          error.message.includes("Cast to ObjectId failed")
        ) {
          setErrorMessages([]);
          setUsingMockData(true);
        } else {
          setErrorMessages([]);
          setUsingMockData(true);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchErrorMessages();
  }, [selectedCampaignId]);

  const [tempMessages, setTempMessages] = useState<{ [key: string]: string }>(
    {}
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const handleInputChange = (id: string, value: string) => {
    setEditingMessages((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Check if there are changes
    const originalMessage =
      errorMessages.find((msg) => msg.error_id === id)?.message || "";
    const hasChange = value !== originalMessage;
    setHasChanges(
      hasChange ||
        Object.keys(editingMessages).some((key) => {
          const original =
            errorMessages.find((msg) => msg.error_id === key)?.message || "";
          return key !== id && editingMessages[key] !== original;
        })
    );
  };

  const handleSaveAll = async () => {
    try {
      setIsLoading(true);
      // Build bulk payload with changed items only
      const changes = Object.entries(editingMessages)
        .filter(([errorId, message]) => {
          const originalMessage =
            errorMessages.find((msg) => msg.error_id === errorId)?.message ||
            "";
          return message !== originalMessage;
        })
        .map(([errorId, message]) => {
          const found = errorMessages.find((m) => m.error_id === errorId);
          const id = (found && (found._id || found.error_id)) || errorId;
          return { id, message };
        });

      if (changes.length > 0) {
        await apiService.updateErrorMessagesBulk({ updates: changes });
      }

      // Refresh data for current campaign
      const response = await apiService.getErrorMessages(selectedCampaignId);
      setErrorMessages(response.data);

      // Reset editing state
      const newMessages: Record<string, string> = {};
      response.data.forEach((msg) => {
        newMessages[msg.error_id] = msg.message;
      });
      setEditingMessages(newMessages);
      setHasChanges(false);

      console.log("Đã lưu thành công tất cả kịch bản!");
    } catch (error: any) {
      console.error("Failed to save error messages:", error);
      setError(error.message || "Không thể lưu kịch bản lỗi");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelAll = () => {
    // Reset to original messages
    const originalMessages: Record<string, string> = {};
    errorMessages.forEach((msg) => {
      originalMessages[msg.error_id] = msg.message;
    });
    setEditingMessages(originalMessages);
    setHasChanges(false);
  };

  // hasChanges is now managed in state

  // Prioritize "Đổi quà thành công" at top before pagination
  const sortedMessages = [...errorMessages].sort((a, b) => {
    const isSuccessA =
      a.error?.name?.trim().toLowerCase() === "đổi quà thành công";
    const isSuccessB =
      b.error?.name?.trim().toLowerCase() === "đổi quà thành công";
    if (isSuccessA && !isSuccessB) return -1;
    if (!isSuccessA && isSuccessB) return 1;
    return 0;
  });

  // Calculate pagination for API data (after sorting)
  const totalItems = sortedMessages.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedMessages.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const unsavedChangesCount = errorMessages.reduce((count, msg) => {
    const edited = editingMessages[msg.error_id];
    return count + (edited !== undefined && edited !== msg.message ? 1 : 0);
  }, 0);

  const errorStats = {
    hasScenario: errorMessages.length,
    noScenario: unsavedChangesCount,
  };

  // Show loading state
  if (isLoading || campaignsLoading) {
    return (
      <div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            {campaignsLoading
              ? "Đang tải danh sách chiến dịch..."
              : "Đang tải dữ liệu lỗi..."}
          </span>
        </div>
      </div>
    );
  }

  // Không chặn toàn trang khi có lỗi – chỉ hiển thị cảnh báo inline bên dưới

  // Show empty state when no error messages
  // if (errorMessages.length === 0) {
  //   return (
  //     <div className="p-6">
  //       {/* Header */}
  //       <div className="mb-6">
  //         <div className="flex items-center justify-between">
  //           <div>
  //             <h1 className="text-2xl font-bold text-gray-900 mb-2">
  //               Kịch bản xử lý thông báo
  //             </h1>
  //             <p className="text-gray-600">
  //               Quản lý và xử lý các loại thông báo trong hệ thống
  //             </p>
  //           </div>
  //         </div>
  //       </div>

  //       {/* Mock data warning */}
  //       {usingMockData && (
  //         <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
  //           <div className="flex items-center">
  //             <svg
  //               className="w-5 h-5 text-yellow-600 mr-2"
  //               fill="none"
  //               stroke="currentColor"
  //               viewBox="0 0 24 24"
  //             >
  //               <path
  //                 strokeLinecap="round"
  //                 strokeLinejoin="round"
  //                 strokeWidth={2}
  //                 d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
  //               />
  //             </svg>
  //             <span className="text-yellow-800">
  //               Đang hiển thị dữ liệu mẫu do lỗi kết nối API. Vui lòng liên hệ
  //               admin để kiểm tra.
  //             </span>
  //           </div>
  //         </div>
  //       )}

  //       {/* Statistics Cards */}
  //       <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
  //         <div className="bg-white px-4 py-4 rounded-lg border border-gray-200 shadow-sm">
  //           <div className="flex items-center">
  //             <div className="p-3 bg-purple-100 rounded-lg">
  //               <svg
  //                 className="w-6 h-6 text-purple-600"
  //                 fill="none"
  //                 stroke="currentColor"
  //                 viewBox="0 0 24 24"
  //               >
  //                 <path
  //                   strokeLinecap="round"
  //                   strokeLinejoin="round"
  //                   strokeWidth={2}
  //                   d="M12 14v8m0 0V6m0 12a5 5 0 005-5H7a5 5 0 005 5z"
  //                 />
  //               </svg>
  //             </div>
  //             <div className="ml-4">
  //               <p className="text-sm font-medium text-gray-600">
  //                 Thông báo có kịch bản
  //               </p>
  //               <p className="text-2xl font-bold text-gray-900">0</p>
  //             </div>
  //           </div>
  //         </div>
  //         <div className="bg-white px-4 py-4 rounded-lg border border-gray-200 shadow-sm">
  //           <div className="flex items-center">
  //             <div className="p-3 bg-orange-100 rounded-lg">
  //               <svg
  //                 className="w-6 h-6 text-orange-600"
  //                 fill="none"
  //                 stroke="currentColor"
  //                 viewBox="0 0 24 24"
  //               >
  //                 <path
  //                   strokeLinecap="round"
  //                   strokeLinejoin="round"
  //                   strokeWidth={2}
  //                   d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
  //                 />
  //               </svg>
  //             </div>
  //             <div className="ml-4">
  //               <p className="text-sm font-medium text-gray-600">
  //                 Thông báo chưa có kịch bản
  //               </p>
  //               <p className="text-2xl font-bold text-gray-900">0</p>
  //             </div>
  //           </div>
  //         </div>
  //       </div>

  //       {/* Empty State */}
  //       <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
  //         <div className="text-center py-12">
  //           <svg
  //             className="mx-auto h-12 w-12 text-gray-400"
  //             fill="none"
  //             stroke="currentColor"
  //             viewBox="0 0 24 24"
  //           >
  //             <path
  //               strokeLinecap="round"
  //               strokeLinejoin="round"
  //               strokeWidth={2}
  //               d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
  //             />
  //           </svg>
  //           <h3 className="mt-2 text-sm font-medium text-gray-900">
  //             Không có lỗi nào
  //           </h3>
  //           <p className="mt-1 text-sm text-gray-500">
  //             Hiện tại chưa có lỗi nào trong hệ thống.
  //           </p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Kịch bản xử lý thông báo
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Quản lý và xử lý các loại thông báo trong hệ thống
            </p>
          </div>
          <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 shadow-sm hover:shadow-md transition-shadow duration-200">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Chiến dịch:
            </span>
            <select
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
              className="text-sm bg-transparent dark:bg-gray-800 border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-white font-semibold min-w-[220px] cursor-pointer"
              // disabled={campaignsLoading || campaigns.length === 0}
            >
              {campaigns.map((c) => (
                <option key={c._id} value={c._id || ""}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Inline warning (chỉ hiện khi vẫn có dữ liệu để hiển thị) */}
      {error && errorMessages.length > 0 && (
        <div className="mb-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <svg
                className="w-6 h-6 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 14v8m0 0V6m0 12a5 5 0 005-5H7a5 5 0 005 5z"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Thông báo có kịch bản
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {errorStats.hasScenario}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 14v8m0 0V6m0 12a5 5 0 005-5H7a5 5 0 005 5z"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Kịch bản đang chỉnh sửa
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {errorStats.noScenario}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Scenarios Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="w-1/2 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Danh sách thông báo
                </th>
                <th className="w-1/2 px-2 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Kịch bản thông báo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {currentItems.map((errorMessage) => {
                const getIconColor = (msg: ErrorMessage) => {
                  const isSuccess =
                    msg.error?.name?.trim().toLowerCase() ===
                    "đổi quà thành công";
                  return isSuccess
                    ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                    : "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400";
                };

                const isSuccess =
                  errorMessage.error?.name?.trim().toLowerCase() ===
                  "đổi quà thành công";

                const hasNewMessage =
                  editingMessages[errorMessage.error_id] !==
                  errorMessage.message;
                const displayMessage =
                  editingMessages[errorMessage.error_id] ||
                  errorMessage.message;

                return (
                  <tr
                    key={errorMessage.error_id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="w-1/2 px-2 py-3">
                      <div className="flex items-center space-x-2">
                        <div
                          className={`w-6 h-6 ${getIconColor(
                            errorMessage
                          )} rounded-full flex items-center justify-center flex-shrink-0`}
                        >
                          {isSuccess ? (
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {errorMessage.error.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="w-1/2 px-2 py-3">
                      <textarea
                        value={displayMessage}
                        onChange={(e) =>
                          handleInputChange(
                            errorMessage.error_id,
                            e.target.value
                          )
                        }
                        className={`w-full p-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent resize-none text-sm ${
                          hasNewMessage
                            ? "border-blue-500 dark:border-blue-400"
                            : "border-gray-300 dark:border-gray-600"
                        }`}
                        rows={2}
                        placeholder="Nhập kịch bản thông báo..."
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination and Save Button */}
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* Pagination Info */}
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Hiển thị {startIndex + 1} đến {Math.min(endIndex, totalItems)}{" "}
                trong tổng số {totalItems} lỗi
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">Hiển thị:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) =>
                    handleItemsPerPageChange(Number(e.target.value))
                  }
                  className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &lt;
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm rounded-md ${
                      currentPage === pageNum
                        ? "bg-blue-600 dark:bg-blue-500 text-white"
                        : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &gt;
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancelAll}
                disabled={!hasChanges}
                className={`px-4 py-2 rounded-md transition-colors flex items-center space-x-2 text-sm ${
                  hasChanges
                    ? "bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700"
                    : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                }`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>Hủy</span>
              </button>
              <button
                onClick={handleSaveAll}
                disabled={!hasChanges}
                className={`px-4 py-2 rounded-md transition-colors flex items-center space-x-2 text-sm ${
                  hasChanges
                    ? "bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-500"
                    : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                }`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
                <span>Lưu lại</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorHandling;
